// pages/Home.js
// Progress matrix home page. A user can be in MULTIPLE concurrent active
// periods (each gated by office/department); we render one group per applicable
// period, each with its own section cards. Each card fetches its own summary
// from /api/diSummary/{period}/{sectionKey} in parallel so a slow PE source for
// one section doesn't block the rest. While a section loads, its % and X/Y
// render as small spinners.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import TopBar from '../components/TopBar';
import { useHeartbeat } from '../hooks/useHeartbeat';
import NlcRequestsPanel from './NlcRequestsPanel';
import './Home.css';

const ROLE_COLORS = {
  originator: '#2a6aad',
  partner:    '#1a7a4a',
  manager:    '#7a4a1a',
};

// Fixed display order regardless of the per-section precedence.
const ROLE_ORDER = { manager: 0, partner: 1, originator: 2 };
function sortRoles(roles) {
  return [...(roles || [])].sort((a, b) =>
    (ROLE_ORDER[a.role] ?? 99) - (ROLE_ORDER[b.role] ?? 99)
  );
}

function Spinner({ inline }) {
  return <span className={`rt-spinner${inline ? ' rt-spinner--inline' : ''}`} aria-label="loading" />;
}

// `emptyIsDone`: a role with nothing assigned reads as complete (full green, 100%) — set only when the card
// has items for another role, so a card with nothing at all (or an unknown 0 / 0) isn't shown as done.
function ProgressBar({ reviewed, total, loading, emptyIsDone }) {
  if (loading) {
    return (
      <div className="progress-track">
        <div className="progress-fill progress-fill--loading" style={{ width: '100%' }} />
      </div>
    );
  }
  const pct = total > 0 ? (reviewed / total) * 100 : emptyIsDone ? 100 : 0;
  const clamped = Math.min(100, Math.max(0, pct));
  const color = clamped >= 100 ? '#1a7a4a' : clamped >= 50 ? '#e6a817' : '#c0392b';
  return (
    <div className="progress-track">
      <div className="progress-fill" style={{ width: `${clamped}%`, background: color }} />
      <span className="progress-label">{Math.round(clamped)}%</span>
    </div>
  );
}

// What one card shows, derived from its summary. Shared by SectionCard and the Active /
// Completed tabs so the two can't disagree. `bucket` is the tab the card belongs in:
//   loading — summary not back yet: shown in neither tab, so a card with nothing to do never flashes up first
//   active  — something to do, or not known (failed / no summary configured)
//   done    — Completed (everything reviewed), No Items (nothing assigned) or a phase not yet released
function cardStatus(sec, summaryEntry, isAdmin) {
  if (sec.phase?.lockedForMe) return { phaseLocked: true, bucket: 'done' };

  const { status, roles: summaryRoles, visibleItems, approvals, noConfig } = summaryEntry || { status: 'loading', roles: {} };
  const loading = status === 'loading';
  const errored = status === 'error';

  // Billing Team viewers of Client Billing Review: the card tracks approval requests (decided / total),
  // always stays openable (the queue also holds history), and never reads "Completed".
  const isApprovals = !!approvals;
  const totalReviewed = isApprovals
    ? Math.max(0, approvals.total - approvals.pending)
    : Object.values(summaryRoles).reduce((a, b) => a + (b.reviewed || 0), 0);
  const totalTotal    = isApprovals
    ? approvals.total
    : Object.values(summaryRoles).reduce((a, b) => a + (b.total    || 0), 0);
  const avgPct        = totalTotal > 0 ? (totalReviewed / totalTotal) * 100 : null;

  // A section may have rows to show that aren't scored against this user — BPPR
  // jobs they handed off, which count in the recipient's queue. Those still need
  // to be reachable (to pull one back), so `visibleItems` keeps the card openable
  // even at 0 / 0. Sections that don't report it behave exactly as before.
  const isEmpty = !isApprovals && !loading && !errored && totalTotal === 0 && !(Number(visibleItems) > 0);
  // Everything assigned to this user is reviewed: the card shows Completed and the
  // section can't be opened. Admins (firm-wide view, Unmark) can still open it; and
  // `visibleItems` beyond the scored total (e.g. handed-off jobs) keeps it open.
  const isComplete = !isApprovals && !loading && !errored && totalTotal > 0 && totalReviewed >= totalTotal
    && !(Number(visibleItems) > totalTotal);
  // Nothing to review disables the button for EVERY section — Dev ones included. The one
  // exception is a section with no summary configured (a stub): there 0 / 0 means "unknown",
  // so it stays openable for the admins who can see it.
  const emptyLocked = isEmpty && !noConfig;
  // Client Billing Review stays openable once complete: that's where a manager UNSUBMITS, opens the report
  // and sees their pending billing changes — locking it out left them no way back in.
  const reopenable = sec.sectionKey === 'client-billing-review';
  const locked = emptyLocked || (isComplete && !isAdmin && !reopenable);

  return {
    summaryRoles, visibleItems, approvals, loading, errored, isApprovals,
    totalReviewed, totalTotal, avgPct, isEmpty, isComplete, locked,
    bucket: loading ? 'loading' : (emptyLocked || isComplete) ? 'done' : 'active',
  };
}

// One section card. `summaryEntry` is { status, roles } for THIS (period, section).
function SectionCard({ period, sec, summaryEntry, userRoles, isAdmin, navigate }) {
  // Phase not released yet (and not an admin — admins bypass it, impersonating or not): no data, no link — just when it opens.
  if (sec.phase?.lockedForMe) {
    return (
      <div className="section-card section-card--phase-locked">
        <div className="section-card-header">
          <div className="section-card-title">
            <i className="fas fa-lock section-icon-lock" />
            {sec.label}
          </div>
        </div>
        <div className="section-phase-note">{sec.phase.message}</div>
        <div className="section-card-footer">
          <button className="section-btn" disabled title={sec.phase.message}>
            <i className="fas fa-lock" /> {sec.phase.message}
          </button>
        </div>
      </div>
    );
  }

  const {
    summaryRoles, approvals, loading, errored, isApprovals,
    totalReviewed, totalTotal, avgPct, isEmpty, isComplete, locked,
  } = cardStatus(sec, summaryEntry, isAdmin);
  const myRoles = new Set(userRoles);
  const sectionRoles = sortRoles(sec.roles);

  return (
    <div className={`section-card ${sec.sectionType === 'external' ? 'section-card--external' : ''} ${isComplete ? 'section-card--complete' : ''}`}>
      <div className="section-card-header">
        <div className="section-card-title">
          {sec.sectionType === 'external' && <i className="fas fa-external-link-alt section-icon-ext" />}
          {sec.sectionType === 'tree'     && <i className="fas fa-sitemap section-icon-tree" />}
          {sec.label}
          {sec.required === false && <span className="section-optional-tag">Optional</span>}
          {sec.phase?.locked && (
            <span className="section-optional-tag" title="Locked for everyone else — you can open it as an admin.">
              {sec.phase.message} · admin preview
            </span>
          )}
        </div>
        {loading ? (
          <Spinner inline />
        ) : errored ? (
          <span className="section-pct section-pct--err" title="Failed to load">—</span>
        ) : isComplete ? (
          <span className="section-complete-badge"><i className="fas fa-check-circle" /> Completed</span>
        ) : avgPct !== null ? (
          <span className={`section-pct ${avgPct >= 100 ? 'done' : ''}`}>{Math.round(avgPct)}%</span>
        ) : null}
      </div>

      <div className="section-roles">
        {isApprovals && (
          <div className="role-row role-row--mine">
            <div className="role-row-label">
              <span className="role-dot" style={{ background: ROLE_COLORS.manager || '#888' }} />
              <span className="role-name">approvals</span>
            </div>
            {/* No count column here, so the bar runs the full width of the row. */}
            <div className="role-row-progress" title={`${approvals.pending} pending of ${approvals.total} requests`}>
              <ProgressBar reviewed={totalReviewed} total={totalTotal} loading={loading} />
            </div>
          </div>
        )}
        {!isApprovals && sectionRoles.map(sr => {
          const isMine = myRoles.has(sr.role);
          const entry  = summaryRoles[sr.role] || { total: 0, reviewed: 0 };
          // Partner bar on a manager→partner section: how many of the partner's items the manager has already signed.
          const showMgrBadge = !loading && !errored && sr.role === 'partner' && Number.isFinite(entry.mgrSigned)
            && entry.total > 0 && entry.reviewed < entry.total;
          return (
            <div key={sr.role} className={`role-row ${isMine ? 'role-row--mine' : ''}`}>
              <div className="role-row-label">
                <span className="role-dot" style={{ background: ROLE_COLORS[sr.role] || '#888' }} />
                <span className="role-name">{sr.role}</span>
              </div>
              <div className="role-row-progress">
                <ProgressBar reviewed={entry.reviewed} total={entry.total} loading={loading} emptyIsDone={!errored && totalTotal > 0} />
              </div>
              <div className="role-row-count">
                {showMgrBadge && (
                  <span className="role-mgr-badge" title="Items the manager has already signed off, out of this partner's total">
                    {entry.mgrSigned} of {entry.total} MGR sign-off
                  </span>
                )}
                {loading ? <Spinner inline /> : errored ? '—' : `${entry.reviewed} / ${entry.total}`}
              </div>
            </div>
          );
        })}
      </div>

      <div className="section-card-footer">
        {sec.sectionType === 'external' ? (
          // External link-outs (IDD/Budgets, Non-Billable Budget Hours) disable
          // when the user has nothing assigned — no point sending them over.
          isEmpty || (isComplete && !isAdmin) ? (
            <button
              className={`section-btn section-btn--external ${isComplete ? 'section-btn--complete' : ''}`}
              disabled
              title={isComplete ? 'You have reviewed everything in this section.' : 'You have no items in this section for this period.'}
            >
              {isComplete ? <><i className="fas fa-check" /> Completed</> : 'Nothing to Review'}
            </button>
          ) : (
            <a
              href={sec.config?.url || '#'}
              target="_blank"
              rel="noreferrer"
              className="section-btn section-btn--external"
            >
              {sec.config?.button_label || 'Open'} <i className="fas fa-external-link-alt" />
            </a>
          )
        ) : (
          <button
            className={`section-btn ${isComplete && locked ? 'section-btn--complete' : ''}`}
            onClick={() => navigate(`/section/${encodeURIComponent(period)}/${sec.sectionKey}`)}
            // Dev sections (admin-only, often stubs with no summary data) stay
            // openable so admins can always reach them; so do completed ones.
            disabled={locked}
            title={locked
              ? (isComplete ? 'You have reviewed everything in this section.' : 'You have no items in this section for this period.')
              : undefined}
          >
            {locked
              ? (isComplete ? <><i className="fas fa-check" /> Completed</> : 'Nothing to Review')
              : 'Open Section'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Home() {
  const { periods, userRoles, isAdmin, email } = useAuth();
  const navigate = useNavigate();

  // summary: { [period]: { [sectionKey]: { status, roles } } }
  const [summary, setSummary] = useState({});
  // Which tab each period group shows: { [period]: 'active' | 'done' } (default Active).
  const [tabs, setTabs] = useState({});

  // Fetch one card. A silent reload (heartbeat) keeps the current numbers on
  // screen and on failure, rather than flashing a spinner or an error.
  const loadCard = useCallback((period, sectionKey, { silent = false } = {}) => {
    fetch(`/api/diSummary/${encodeURIComponent(period)}/${sectionKey}`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        setSummary(prev => ({
          ...prev,
          [period]: {
            ...(prev[period] || {}),
            [sectionKey]: {
              status: 'loaded', roles: data.roles || {}, visibleItems: data.visibleItems, approvals: data.approvals || null,
              noConfig: data.note === 'no summary config',
            },
          },
        }));
      })
      .catch(err => {
        console.error(`summary fetch error [${period}/${sectionKey}]`, err);
        if (silent) return;
        setSummary(prev => ({
          ...prev,
          [period]: { ...(prev[period] || {}), [sectionKey]: { status: 'error', roles: {} } },
        }));
      });
  }, []);

  useEffect(() => {
    if (!periods?.length) return;

    // Seed loading state for every (period, section) so spinners appear at once.
    // A section whose phase hasn't released yet (lockedForMe) has no card data — skip it.
    const seed = {};
    for (const p of periods) {
      seed[p.period] = {};
      for (const sec of p.sections || []) {
        if (sec.phase?.lockedForMe) continue;
        seed[p.period][sec.sectionKey] = { status: 'loading', roles: {} };
      }
    }
    setSummary(seed);

    // One fetch per (period, section); resolve each card independently.
    for (const p of periods) {
      for (const sec of p.sections || []) {
        if (!sec.phase?.lockedForMe) loadCard(p.period, sec.sectionKey);
      }
    }
  }, [periods, email, loadCard]);

  // Live cards: the app heartbeat reports sign-offs/edits by other people per
  // (period, section). Reload just those cards; an NLC mark, a rebuilt snapshot
  // or an overflowed change list reloads every card.
  const allSectionKeys = useMemo(
    () => [...new Set((periods || []).flatMap(p => (p.sections || []).map(s => s.sectionKey)))],
    [periods],
  );
  // section_key -> label, so a request can say where it was filed from.
  const sectionLabels = useMemo(() => {
    const m = {};
    for (const p of periods || []) for (const s of p.sections || []) m[s.sectionKey] = s.label;
    return m;
  }, [periods]);

  useHeartbeat(periods?.length ? {
    sections: allSectionKeys,
    onChange: ({ changes, versionChanged, truncated }) => {
      const everything = versionChanged || truncated || changes.some(c => c.global);
      for (const p of periods) {
        for (const sec of p.sections || []) {
          if (sec.phase?.lockedForMe) continue;
          const hit = everything || changes.some(c => c.period === p.period && c.sectionKey === sec.sectionKey);
          if (hit) loadCard(p.period, sec.sectionKey, { silent: true });
        }
      }
    },
  } : null);

  return (
    <>
      <TopBar />
      <main className="home-main">
        <div className="home-header">
          <div>
            <h1 className="home-title">Data Integrity</h1>
          </div>
          {isAdmin && (
            <button className="home-admin-btn" onClick={() => navigate('/admin')}>
              <i className="fas fa-cog" /> Manage Periods &amp; Sections
            </button>
          )}
        </div>

        {periods.length === 0 ? (
          <div className="home-empty">No active review cycle applies to you right now.</div>
        ) : (
          periods.map(p => {
            // Active = something to do (or not known yet); Completed / No Items = all reviewed,
            // nothing assigned, or a phase that hasn't released yet.
            const buckets = { loading: [], active: [], done: [] };
            for (const sec of p.sections || []) {
              buckets[cardStatus(sec, summary[p.period]?.[sec.sectionKey], isAdmin).bucket].push(sec);
            }
            const tab = tabs[p.period] || 'active';
            const shown = buckets[tab];
            const stillLoading = buckets.loading.length > 0;
            return (
              <section key={p.period} className="home-period-group">
                <div className="home-period-group-header">
                  <span className="home-period-badge active">{p.label || p.period}</span>
                  {p.description && <span className="home-period-desc">{p.description}</span>}
                </div>
                {(p.sections || []).length === 0 ? (
                  <div className="home-empty">No sections are enabled for this cycle.</div>
                ) : (
                  <>
                    <div className="home-tabs" role="tablist">
                      {stillLoading && (
                        <div className="home-loading-overlay" role="status" aria-live="polite">
                          <Spinner inline /> Loading your sections…
                        </div>
                      )}
                      {[['active', 'Active'], ['done', 'Completed / No Items']].map(([key, label]) => (
                        <button key={key} type="button" role="tab" aria-selected={tab === key}
                                className={`home-tab${tab === key ? ' home-tab--on' : ''}`}
                                onClick={() => setTabs(prev => ({ ...prev, [p.period]: key }))}>
                          {label} {!stillLoading && <span className="home-tab-count">{buckets[key].length}</span>}
                        </button>
                      ))}
                    </div>
                    {shown.length === 0 && stillLoading ? null : shown.length === 0 ? (
                      <div className="home-tab-empty">
                        {tab === 'active'
                          ? 'Nothing left to review in this cycle.'
                          : 'No completed sections yet.'}
                      </div>
                    ) : (
                      <div className="section-grid">
                        {shown.map(sec => (
                          <SectionCard
                            key={`${p.period}::${sec.sectionKey}`}
                            period={p.period}
                            sec={sec}
                            summaryEntry={summary[p.period]?.[sec.sectionKey]}
                            userRoles={userRoles}
                            isAdmin={isAdmin}
                            navigate={navigate}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </section>
            );
          })
        )}

        {/* Partner's NLC requests to confirm (+ the viewer's own pending ones) — below the section cards,
            independent of any open section. */}
        <NlcRequestsPanel sectionLabels={sectionLabels} />
      </main>
    </>
  );
}
